import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Box, Typography, Tabs, Tab, Button, CircularProgress } from '@mui/material';
import { walletAPI, renewalsAPI } from '../../services/api';
import { useRecommendations } from '../../hooks/useRecommendations';
import RenewalComparison from '../../components/renewals/RenewalComparison';
import PremiumHistoryChart from '../../components/renewals/PremiumHistoryChart';
import PremiumHistoryTable from '../../components/renewals/PremiumHistoryTable';
import { maskPolicyNumber } from '../../utils/renewalHelpers';
import { getErrorMessage } from '../../utils/renewalErrors';
import { FEATURES } from '../../config/features';
import t from '../../utils/renewalsTranslations';

const locale = (typeof window !== 'undefined' && localStorage.getItem('locale')) || 'en';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ py: 2 }}>{children}</Box> : null;
}

export default function RenewalDetails() {
  const { id: policyId } = useParams();
  const navigate = useNavigate();
  const [policy, setPolicy] = useState(null);
  const [tab, setTab] = useState(0);
  const [loadingPolicy, setLoadingPolicy] = useState(true);
  const [switchLoading, setSwitchLoading] = useState(false);
  const { list: recommendations, best, loading: loadingRecs, reload: reloadRecs } = useRecommendations(policyId);

  useEffect(() => {
    if (!policyId) return;
    (async () => {
      setLoadingPolicy(true);
      try {
        const data = await walletAPI.getPolicy(policyId);
        setPolicy(data?.data ?? data);
      } catch (e) {
        setPolicy(null);
      } finally {
        setLoadingPolicy(false);
      }
    })();
  }, [policyId]);

  const handleSwitch = async (recId) => {
    setSwitchLoading(true);
    try {
      await renewalsAPI.switchToNew(policyId, recId);
      reloadRecs();
    } catch (e) {
      console.warn(e);
    } finally {
      setSwitchLoading(false);
    }
  };

  const handleRenew = async () => {
    setSwitchLoading(true);
    try {
      await renewalsAPI.renewWithCurrent(policyId);
      reloadRecs();
    } catch (e) {
      console.warn(e);
    } finally {
      setSwitchLoading(false);
    }
  };

  if (loadingPolicy) return <Container sx={{ py: 3 }}><CircularProgress /></Container>;
  if (!policy) return <Container sx={{ py: 3 }}><Typography color="error">{getErrorMessage('policy_not_found')}</Typography><Button onClick={() => navigate('/renewals')}>View All Renewals</Button></Container>;

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{policy.policy_name || maskPolicyNumber(policy.policy_number)}</Typography>
        <Button size="small" onClick={() => navigate(`/renewals/${policyId}/settings`)}>{t('renewals.settings.title')}</Button>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label={t('renewals.comparison.title')} />
        <Tab label={t('renewals.settings.title')} />
        <Tab label="Premium history" />
        <Tab label="History" />
      </Tabs>

      <TabPanel value={tab} index={0}>
        {loadingRecs ? <CircularProgress /> : (
          best ? (
            <RenewalComparison
              currentPolicy={policy}
              recommendation={best}
              onSwitch={handleSwitch}
              onRenew={handleRenew}
              loading={switchLoading}
            />
          ) : (
            <Typography color="text.secondary">{t('renewals.comparison.noRecommendations')}</Typography>
          )
        )}
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <Button variant="outlined" onClick={() => navigate(`/renewals/${policyId}/settings`)}>
          {t('renewals.settings.back')}
        </Button>
      </TabPanel>

      <TabPanel value={tab} index={2}>
        <PremiumHistoryTab policyId={policyId} />
      </TabPanel>

      <TabPanel value={tab} index={3}>
        <Button size="small" onClick={() => navigate(`/renewals/${policyId}/history`)}>View recommendation history</Button>
      </TabPanel>
    </Container>
  );
}

function PremiumHistoryTab({ policyId }) {
  const [premiumHistory, setPremiumHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!policyId) return;
    renewalsAPI.getPremiumHistory(policyId).then((d) => setPremiumHistory(Array.isArray(d) ? d : [])).catch(() => setPremiumHistory([])).finally(() => setLoading(false));
  }, [policyId]);
  if (loading) return <CircularProgress />;
  const showChart = FEATURES.premiumHistoryChart;
  return (
    <Box>
      {premiumHistory.length > 0 ? (
        <Box sx={{ height: 400, mb: 4 }}>
          {showChart ? (
            <PremiumHistoryChart premiumHistory={premiumHistory} locale={locale} maxMonths={24} />
          ) : (
            <PremiumHistoryTable data={premiumHistory} />
          )}
        </Box>
      ) : (
        <Typography color="text.secondary">{t('renewals.history.noPremiumHistory')}</Typography>
      )}
    </Box>
  );
}
